import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tv, CheckCircle2, Loader2, X, AlertTriangle, Music2, Instagram, ShoppingBag, Sparkles, Package, Lock } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATAFORMAS = [
  {
    id: "tiktok",
    nome: "TikTok",
    Icone: Music2,
    corFundoIcone: "bg-gradient-to-br from-neutral-800 to-black",
    corGlow: "shadow-[0_0_24px_-4px_rgba(255,255,255,0.15)]",
    aviso: "O TikTok exige participar de uma Creator Network (gratuita) para liberar a chave de transmissão. Sem isso, a live pode ter visibilidade restrita.",
    ajuda: [
      "Entre em uma Creator Network gratuita do TikTok (agência credenciada) - é um passo obrigatório do próprio TikTok.",
      "No app do TikTok, vá em Perfil → Menu (☰) → Criador → Transmissão ao vivo → Transmitir de PC/Console.",
      "O TikTok vai mostrar um \"Endereço do servidor\" e uma \"Chave de transmissão\".",
      "Copie a Chave de transmissão e cole aqui embaixo.",
    ],
  },
  {
    id: "instagram",
    nome: "Instagram",
    Icone: Instagram,
    corFundoIcone: "bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500",
    corGlow: "shadow-[0_0_24px_-4px_rgba(217,70,160,0.4)]",
    aviso: null,
    ajuda: [
      "Use um programa de transmissão para gerar sua chave (o Instagram não mostra a chave dentro do próprio app).",
      "No Instagram, vá em criar Live e escolha a opção de transmitir de outro programa, se disponível na sua conta.",
      "Copie a Chave de transmissão fornecida e cole aqui embaixo.",
    ],
  },
  {
    id: "shopee",
    nome: "Shopee",
    Icone: ShoppingBag,
    corFundoIcone: "bg-gradient-to-br from-orange-500 to-orange-700",
    corGlow: "shadow-[0_0_24px_-4px_rgba(234,88,12,0.4)]",
    aviso: "A Shopee precisa aprovar seu acesso à transmissão via PC antes de gerar a chave.",
    ajuda: [
      "Acesse o Shopee Live pelo Seller Center e solicite acesso à transmissão via PC (aprovação da própria Shopee).",
      "Depois de aprovado, crie uma nova transmissão e selecione os produtos que vai vender.",
      "A Shopee vai mostrar o \"Server\" e a \"Stream Key\".",
      "Copie a Stream Key e cole aqui embaixo.",
    ],
  },
  {
    id: "amazon",
    nome: "Amazon",
    Icone: Package,
    corFundoIcone: "bg-gradient-to-br from-neutral-700 to-neutral-900",
    corGlow: "shadow-[0_0_24px_-4px_rgba(255,153,0,0.3)]",
    aviso: "A Amazon Live hoje funciona pelo aplicativo do celular (iOS) - a conexão direta por chave ainda não é aberta ao público.",
    ajuda: [
      "Baixe o aplicativo Amazon Live (disponível para iOS) e faça login com sua conta de vendedor.",
      "Escolha os produtos que vai apresentar na transmissão.",
      "Toque em \"Go Live\" para começar - hoje esse é o único caminho oficial da Amazon.",
    ],
  },
];

const PLATAFORMA_EM_BREVE = {
  id: "mercado_livre",
  nome: "Mercado Livre",
  Icone: Lock,
  corFundoIcone: "bg-gradient-to-br from-yellow-500 to-yellow-700",
  mensagem: "O Mercado Livre está testando live shopping com vendedores selecionados. Ainda não é aberto ao público - deixamos reservado aqui para ativar assim que abrir.",
};

/**
 * Conexão de transmissão - o afiliado nunca precisa sair do ClickHub
 * para vender ao vivo. Visual elevado por pedido do dono do sistema
 * ("nivel Vale do Silicio") - camadas, glow sutil por plataforma,
 * hierarquia mais rica - mantendo a paleta e os componentes do sistema.
 */
const ConexaoTransmissao = ({ usuarioId }) => {
  const [conexoes, setConexoes] = useState([]);
  const [ajudaAberta, setAjudaAberta] = useState(null);
  const [chaveDigitada, setChaveDigitada] = useState({});
  const [salvandoPlataforma, setSalvandoPlataforma] = useState(null);

  const carregar = async () => {
    if (!usuarioId) return;
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/social/transmissao/${usuarioId}`);
      if (resp.ok) setConexoes(await resp.json());
    } catch (error) {
      console.error("Erro ao carregar conexões de transmissão:", error);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line
  }, [usuarioId]);

  const conectado = (plataformaId) => conexoes.some((c) => c.plataforma === plataformaId);

  const conectar = async (plataformaId) => {
    const chave = chaveDigitada[plataformaId];
    if (!chave) {
      toast.error("Cole a chave de transmissão primeiro.");
      return;
    }
    setSalvandoPlataforma(plataformaId);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/social/transmissao/${usuarioId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plataforma: plataformaId, chave_rtmp: chave }),
      });
      if (!resp.ok) throw new Error("Erro ao conectar.");
      const nomePlataforma = PLATAFORMAS.find((p) => p.id === plataformaId)?.nome || plataformaId;
      toast.success(`Conectado ao ${nomePlataforma}!`);
      setChaveDigitada((prev) => ({ ...prev, [plataformaId]: "" }));
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoPlataforma(null);
    }
  };

  const desconectar = async (plataformaId) => {
    if (!window.confirm("Desconectar esta plataforma?")) return;
    try {
      await authService.fetchAuth(`${BACKEND_URL}/api/social/transmissao/${usuarioId}/${plataformaId}`, { method: "DELETE" });
      toast.success("Desconectado.");
      await carregar();
    } catch (error) {
      toast.error("Erro ao desconectar.");
    }
  };

  const totalConectadas = conexoes.length;

  return (
    <div className="relative rounded-2xl mb-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-purple-500/5" />
      <Card className="relative bg-gray-900/60 backdrop-blur-xl border-gray-800/80 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/20 flex items-center justify-center">
              <Tv className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm tracking-tight">Central de transmissão</p>
              <p className="text-[11px] text-gray-500">Venda ao vivo sem sair da ClickHub</p>
            </div>
          </div>
          {totalConectadas > 0 && (
            <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400 text-[11px] font-medium">{totalConectadas} ativa{totalConectadas > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent my-4" />

        <div className="grid md:grid-cols-4 gap-3">
          {PLATAFORMAS.map((p) => {
            const Icone = p.Icone;
            const jaConectado = conectado(p.id);
            return (
              <div
                key={p.id}
                className={`relative rounded-xl border p-4 transition-all duration-300 ${
                  jaConectado
                    ? "border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.07] to-transparent"
                    : "border-gray-800 bg-gray-950/40 hover:border-gray-700"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-full ${p.corFundoIcone} ${p.corGlow} flex items-center justify-center shrink-0`}>
                      <Icone className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm text-white font-semibold tracking-tight">{p.nome}</span>
                    <button
                      onClick={() => setAjudaAberta(ajudaAberta === p.id ? null : p.id)}
                      className="w-4 h-4 rounded-full border border-gray-600 text-gray-400 text-[10px] font-bold flex items-center justify-center hover:border-orange-500 hover:text-orange-400 hover:bg-orange-500/10 transition-all"
                    >
                      ?
                    </button>
                  </div>
                  {jaConectado && (
                    <span className="text-emerald-400 text-[10px] font-medium flex items-center gap-1 bg-emerald-950/60 border border-emerald-800/40 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-2.5 h-2.5" /> Ativo
                    </span>
                  )}
                </div>

                {p.aviso && (
                  <div className="flex items-start gap-2 bg-amber-950/20 border border-amber-800/30 rounded-lg p-2.5 mb-3">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-amber-400/90 text-[11px] leading-relaxed">{p.aviso}</p>
                  </div>
                )}

                {ajudaAberta === p.id && (
                  <div className="bg-gray-950/80 border border-orange-600/40 rounded-lg p-3 mb-3">
                    <p className="text-orange-400 text-xs font-semibold mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> Como conectar
                    </p>
                    <ol className="text-gray-400 text-[11px] leading-relaxed list-decimal list-inside space-y-1.5">
                      {p.ajuda.map((passo, i) => (
                        <li key={i}>{passo}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {jaConectado ? (
                  <Button size="sm" variant="outline" onClick={() => desconectar(p.id)} className="w-full border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 text-xs">
                    <X className="w-3 h-3 mr-1" /> Desconectar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      placeholder="Chave de transmissão"
                      value={chaveDigitada[p.id] || ""}
                      onChange={(e) => setChaveDigitada((prev) => ({ ...prev, [p.id]: e.target.value }))}
                      className="h-8 text-xs bg-gray-950/60 border-gray-800 focus:border-orange-600/50"
                    />
                    <Button
                      size="sm"
                      onClick={() => conectar(p.id)}
                      disabled={salvandoPlataforma === p.id}
                      className="h-8 bg-gradient-to-b from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 shrink-0 shadow-lg shadow-orange-900/30"
                    >
                      {salvandoPlataforma === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Conectar"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          <div className="relative rounded-xl border border-dashed border-gray-800 p-4 flex flex-col items-center justify-center text-center opacity-60">
            <div className={`w-9 h-9 rounded-full ${PLATAFORMA_EM_BREVE.corFundoIcone} flex items-center justify-center mb-2`}>
              <PLATAFORMA_EM_BREVE.Icone className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-white font-semibold tracking-tight mb-1">{PLATAFORMA_EM_BREVE.nome}</span>
            <span className="text-[10px] text-gray-500 bg-gray-900 border border-gray-800 px-2 py-0.5 rounded-full mb-2">Em breve</span>
            <p className="text-gray-500 text-[10px] leading-relaxed">{PLATAFORMA_EM_BREVE.mensagem}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ConexaoTransmissao;
